// About — film. One job: keep YouTube off the page until someone asks for it.
// The control is a real link to the watch page, so with the script off, or
// before it runs, the film is still reachable. Everything here is an upgrade
// on that link, never a replacement for it.

function idOf(figure) {
  const raw = figure.getAttribute('data-video-id') || '';
  return raw.trim();
}

// Wide enough for a lightbox to be worth it. Below this the film plays in
// place, where the reader already is.
const WIDE = '(min-width: 750px)';

function playerFor(id, label) {
  const iframe = document.createElement('iframe');
  iframe.src =
    'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
  if (label) iframe.title = label;
  iframe.allow =
    'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share';
  iframe.allowFullscreen = true;
  iframe.setAttribute('loading', 'lazy');
  return iframe;
}

function load(figure, trigger, id) {
  if (figure.dataset.loaded === 'true') return;
  figure.dataset.loaded = 'true';

  // The owner's own label, not the whole poster's text.
  const label = (trigger.getAttribute('data-video-title') || '').trim();
  const iframe = playerFor(id, label);

  trigger.replaceWith(iframe);
  // preventScroll: the player is often mid-viewport and a jump loses the reader's place.
  iframe.focus({ preventScroll: true });
}

// Desktop: the film opens over the page instead of squeezing into the column.
// <dialog> is doing the work — Escape, the backdrop and the focus trap are all
// native, so there is no keyboard handling to get wrong here.
function openLightbox(figure, trigger, id) {
  if (typeof HTMLDialogElement !== 'function') return false;

  const label = (trigger.getAttribute('data-video-title') || '').trim();
  const dialog = document.createElement('dialog');
  dialog.className = 'about-video__lightbox';

  const frame = document.createElement('div');
  frame.className = 'about-video__lightbox-frame';
  frame.appendChild(playerFor(id, label));

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'about-video__lightbox-close';
  close.innerHTML = '<span aria-hidden="true">&times;</span>';
  close.setAttribute('aria-label', figure.getAttribute('data-close-label') || 'Close the film');
  close.addEventListener('click', () => dialog.close());

  dialog.append(close, frame);

  // A click on the backdrop is a click on the dialog itself.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  const scrollY = window.scrollY;
  dialog.addEventListener('close', () => {
    dialog.remove();
    document.documentElement.style.removeProperty('overflow');
    // Focus goes back where it came from, at the place the reader left.
    trigger.focus({ preventScroll: true });
    window.scrollTo({ top: scrollY, behavior: 'instant' });
  });

  document.body.appendChild(dialog);
  document.documentElement.style.overflow = 'hidden';
  dialog.showModal();
  return true;
}

function play(figure, trigger, id) {
  // data-video-inline: the placement wants the film in its own frame at every
  // width. It also means the lightbox CSS need not be on the page.
  const inline = figure.hasAttribute('data-video-inline');
  if (!inline && window.matchMedia?.(WIDE).matches && openLightbox(figure, trigger, id)) return;
  load(figure, trigger, id);
}

function setUp(figure) {
  if (figure.dataset.videoReady === 'true') return;
  const trigger = figure.querySelector('[data-video-trigger]');
  const id = idOf(figure);
  if (!trigger || !id) return;
  figure.dataset.videoReady = 'true';

  // A still that 404s would otherwise show as a broken image on the frame.
  const thumb = figure.querySelector('[data-video-thumb]');
  if (thumb) {
    thumb.addEventListener(
      'error',
      () => {
        trigger.classList.add('about-video__frame--nothumb');
      },
      { once: true }
    );
  }

  // Enter on a link fires click on its own; this covers both.
  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    play(figure, trigger, id);
  });

  // Space is not a link key by default, and people reach for it on a player.
  trigger.addEventListener('keydown', (event) => {
    if (event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    play(figure, trigger, id);
  });
}

function boot(scope) {
  scope.querySelectorAll('[data-about-video]').forEach(setUp);
}

boot(document);

// The editor rebuilds a section in place; a module script runs only once.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) boot(event.target);
});
